"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Check, ExternalLink, Eye, Lock, ShieldCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
export default function McafeePromo() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 30
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };
  const checklistItems = ["Real-time antivirus and malware protection", "Identity theft monitoring and alerts", "Built-in password manager and safe browsing"];
  return <section id="mcafeepromo" data-nav="dark" className="relative overflow-hidden bg-[hsl(221,40%,4%)] text-white py-20 md:py-28">
      {/* Decorative background watermark */}
      <div aria-hidden="true" className="pointer-events-none absolute -right-8 -top-14 select-none font-mono text-[14rem] sm:text-[20rem] font-bold text-white/[0.03] leading-none">
        02
      </div>

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-16">
          {/* Left Column */}
          <div className="flex flex-col gap-6 lg:col-span-7">
            <motion.div variants={itemVariants}>
              <span className="font-mono text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-widest text-primary uppercase">
                McAfee
              </span>
            </motion.div>

            <motion.h2 variants={itemVariants} className="font-default text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
              All-in-one protection for every device
            </motion.h2>

            <motion.p variants={itemVariants} className="text-base text-zinc-300 sm:text-lg max-w-2xl leading-relaxed">
              Shield your laptops, phones, and personal accounts against malware,
              suspicious links, and identity leaks. McAfee combines real-time device
              antivirus with active credit and dark web monitoring so your digital
              life stays locked down.
            </motion.p>

            {/* Trust pills */}
            <motion.div variants={itemVariants} className="flex flex-wrap items-center gap-3 pt-2">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs sm:text-sm text-zinc-200">
                <ShieldCheck className="size-4 text-primary shrink-0" />
                <span>Real-time threat detection</span>
              </div>
              <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs sm:text-sm text-zinc-200">
                <Eye className="size-4 text-primary shrink-0" />
                <span>Identity monitoring</span>
              </div>
            </motion.div>
          </div>

          {/* Right Column: Featured Card */}
          <motion.div variants={itemVariants} className="lg:col-span-5">
            <Card className="relative overflow-hidden border border-white/15 bg-zinc-950/80 text-white shadow-2xl backdrop-blur-sm rounded-xl py-6">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between gap-3 mb-2">
                  <div className="flex size-11 items-center justify-center rounded-lg bg-primary/10 text-primary border border-primary/20">
                    <Lock className="size-5" />
                  </div>
                  <Badge variant="outline" className="border-primary/40 bg-primary/10 text-primary font-mono text-[11px] uppercase tracking-wider py-1 px-2.5 rounded">
                    Best for All-in-One Antivirus & Identity Protection
                  </Badge>
                </div>
                <CardTitle className="text-xl sm:text-2xl font-bold text-white pt-2">
                  Total Security Suite
                </CardTitle>
                <CardDescription className="text-zinc-400 text-sm">
                  Complete digital defense designed for individuals and families.
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-4">
                <ul className="space-y-3" aria-label="McAfee feature highlights">
                  {checklistItems.map((feature, i) => <li key={feature} data-index={i} className="flex items-start gap-3 text-sm text-zinc-200">
                      <div className="mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full bg-primary/20 text-primary">
                        <Check className="size-3 stroke-[2.5]" />
                      </div>
                      <span>{feature}</span>
                    </li>)}
                </ul>
              </CardContent>

              <CardFooter className="pt-3">
                <Button asChild size="lg" className="w-full bg-primary text-primary-foreground font-semibold hover:bg-primary/90 shadow-md">
                  <Link to="https://vpngenix.com/go/McAfee" newTab className="inline-flex items-center justify-center gap-2">
                    <span>Visit Site</span>
                    <ExternalLink className="size-4" />
                  </Link>
                </Button>
              </CardFooter>
            </Card>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}